import React, { useEffect, useId, useState } from 'react';
import RichText from '../richText/RichText.jsx';
import { canonicalArticleSource, canonicalArticleStudyView, verifyCanonicalArticle } from './canonicalArticleStudy.js';

const prose = 'font-serif text-book-text leading-relaxed break-words';

// Props contract:
// - sourceItem/annotationItem: {id, ...document} from the parent's actually
//   authorised paidItems, with each scope explicitly supplied as 'paidItems'.
// - entitled: current resolved reader access (never inferred from item presence).
// - mode: 'full' (default) or 'opening'; openingB is approved alternative prose
//   supplied at runtime, never another copy of the canonical first paragraph.
// - onReturn(): optional return action; onNavigate('practise'): optional next action.
// This component has no storage, submission, AI, access or content-write path.
export default function CanonicalArticleStudy(props) {
    const source = canonicalArticleSource(props);
    if (!props.entitled) return <p className="font-serif text-book-muted">Unlock this pack to study its Article model.</p>;
    if (!source) return <p role="status" className="font-serif text-book-muted">The paid Article model is not available in the current reader content.</p>;
    return <AuthorizedStudy {...props} source={source} />;
}

function AuthorizedStudy(props) {
    const { source, onReturn, onNavigate } = props;
    const [verification, setVerification] = useState(null);
    const [reveal, setReveal] = useState(null);
    const regionId = useId();
    const view = canonicalArticleStudyView({ ...props, verification });
    const revealed = reveal?.sampleEssay === source.sampleEssay && reveal?.mode === view.mode;

    useEffect(() => {
        let active = true;
        verifyCanonicalArticle(source.sampleEssay).then(result => { if (active) setVerification(result); });
        return () => { active = false; };
    }, [source.sampleEssay]);

    const versionMessage = view.state === 'checking' ? 'Checking the model version…'
        : view.state === 'changed' ? 'The current model has changed. You can read it below; guide annotations and the opening comparison are withheld because they refer to an earlier version.'
            : view.state === 'unavailable' ? 'The model version could not be checked. You can read the current model below; guide annotations and the opening comparison are withheld.'
                : !view.annotationsAvailable ? 'The model is available. Its paragraph guide is not available in the current reader content.' : '';

    return <section className="space-y-5 min-w-0" aria-label={view.mode === 'opening' ? 'Study Article openings' : 'Study an Article model'}>
        <div>
            <h2 className="font-display text-book-accent text-xl mb-3">{view.mode === 'opening' ? 'Study the opening' : 'Study a complete Article model'}</h2>
            <h3 className="font-display text-book-muted text-xs mb-2 font-semibold">Article question</h3>
            <RichText text={source.prompt} className={prose} annotate={false} />
        </div>
        {versionMessage && <p role="status" className="font-serif text-book-muted text-sm leading-relaxed">{versionMessage}</p>}
        <button type="button" disabled={view.state === 'checking'} aria-expanded={revealed} aria-controls={regionId}
            onClick={() => setReveal(revealed ? null : { sampleEssay: source.sampleEssay, mode: view.mode })}
            className="px-4 py-2 border border-book-accent/40 text-book-accent font-display text-sm hover:bg-book-accent/10 disabled:opacity-40">
            {revealed ? 'Hide model' : view.state === 'matched' && view.mode === 'opening' ? 'Reveal canonical opening' : 'Reveal current model'}
        </button>
        {revealed && <div id={regionId} className="space-y-5" aria-label="Article model for study">
            {view.paragraphs.length ? <>
                {view.mode === 'full' && <h3 className="font-display text-book-accent text-lg break-words">{view.headline}</h3>}
                {view.overview && <div className="border border-book-accent/25 bg-book-page/40 p-4">
                    <h3 className="font-display text-book-accent text-sm mb-2">{view.overview.heading}</h3>
                    <RichText text={view.overview.body} className={prose} annotate={false} />
                </div>}
                {view.paragraphs.map((paragraph, index) => <section key={index} className="border border-book-accent/25 bg-book-page/40 p-4 md:p-5 space-y-3">
                    <h3 className="font-display text-book-accent text-sm">{paragraph.annotation?.heading || `Paragraph ${index + 1}`}</h3>
                    <RichText text={paragraph.text} className={prose} annotate={false} />
                    {paragraph.annotation && <details className="border-t border-book-accent/20 pt-3">
                        <summary className="cursor-pointer text-book-accent font-serif text-sm">Read the teacher annotation</summary>
                        <RichText text={paragraph.annotation.body} className={prose + ' mt-3'} annotate={false} />
                    </details>}
                </section>)}
                {view.openingB && <details className="border border-book-accent/25 p-4">
                    <summary className="cursor-pointer font-display text-book-accent text-sm">Compare with alternative Opening B</summary>
                    <RichText text={view.openingB} className={prose + ' mt-3'} annotate={false} />
                </details>}
            </> : <RichText text={source.sampleEssay} className={prose} annotate={false} />}
        </div>}
        {(onReturn || onNavigate) && <div className="flex flex-wrap gap-3 border-t border-book-accent/20 pt-4">
            {onReturn && <button type="button" onClick={onReturn} className="px-3 py-2 border border-book-accent/30 text-book-accent text-sm">Return to Article route</button>}
            {onNavigate && <button type="button" onClick={() => onNavigate('practise')} className="px-3 py-2 border border-book-accent/30 text-book-accent text-sm">Continue with Article practice</button>}
        </div>}
    </section>;
}
